import {
  collection,
  doc,
  getDocs,
  getDoc,
  writeBatch,
  updateDoc,
  increment,
  serverTimestamp,
  runTransaction
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { MOVEMENT_TYPES } from "../../inventory/models/inventory.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { diffInvoice } from "../utils/invoiceEdit.js";

const INVOICES_COLLECTION = "invoices";
const ITEMS_COLLECTION = "items";
const MOVEMENTS_COLLECTION = "inventoryMovements";
const CUSTOMERS_COLLECTION = "customers";

/**
 * Creates an invoice and atomically decrements inventory stock for physical goods,
 * recording corresponding audit log entries in inventoryMovements.
 */
export async function createInvoiceWithStockDeduction(invoiceData) {
  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Create Invoice Document
  const invoiceRef = doc(collection(db, INVOICES_COLLECTION));
  const fullInvoicePayload = {
    ...invoiceData,
    createdBy: user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  batch.set(invoiceRef, fullInvoicePayload);

  // 2. Decrement Item Stock & Record OUT Movements for physical goods
  const lineItems = invoiceData.lineItems || [];
  lineItems.forEach((item) => {
    if (item.itemId && item.itemType !== "service") {
      const itemRef = doc(db, ITEMS_COLLECTION, item.itemId);
      const qty = Number(item.qty || 0);

      // Decrement stock using atomic increment(-qty)
      batch.update(itemRef, {
        stock: increment(-qty),
        updatedAt: serverTimestamp()
      });

      // Add movement audit record
      const movementRef = doc(collection(db, MOVEMENTS_COLLECTION));
      batch.set(movementRef, {
        itemId: item.itemId,
        itemName: item.itemName || "",
        itemSku: item.gstCode || "",
        type: MOVEMENT_TYPES.OUT,
        quantity: qty,
        previousStock: null, // Asynchronous atomic decrement
        newStock: null,
        reason: "Sales Invoice",
        notes: `Invoice ${invoiceData.invoiceNo || invoiceRef.id}`,
        referenceId: invoiceRef.id,
        createdBy: user,
        createdAt: serverTimestamp()
      });
    }
  });

  // 3. Update Customer Outstanding Balance if invoice has unpaid balance
  if (invoiceData.customerId && invoiceData.balance > 0) {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, invoiceData.customerId);
    batch.update(customerRef, {
      openingBalance: increment(Number(invoiceData.balance || 0)),
      updatedAt: serverTimestamp()
    });
  }

  // 4. Update Customer Item Price memory so future invoices for this customer auto-fill these rates
  if (invoiceData.customerId && invoiceData.updatedItemPrices && Object.keys(invoiceData.updatedItemPrices).length > 0) {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, invoiceData.customerId);
    const priceUpdates = { updatedAt: serverTimestamp() };
    for (const [itemId, rate] of Object.entries(invoiceData.updatedItemPrices)) {
      priceUpdates[`customPrices.${itemId}`] = Number(rate);
    }
    batch.update(customerRef, priceUpdates);
  }

  // 5. Atomically increment issuing company's next sequence (GST or Non-GST)
  if (invoiceData.companyId) {
    const isNonGst = invoiceData.isNonGst || invoiceData.invoiceType === "Normal";
    const companyRef = doc(db, "companies", invoiceData.companyId);
    try {
      const companySnap = await getDoc(companyRef);
      if (companySnap.exists()) {
        const compData = companySnap.data();
        const rawNumMatch = String(invoiceData.invoiceNo || "").match(/\d+$/);
        const parsedInvoiceNum = rawNumMatch ? parseInt(rawNumMatch[0], 10) : 0;

        if (isNonGst) {
          const currentSeq = Number(compData.nonGstNextInvoiceNumber || 1);
          const nextSeq = Math.max(currentSeq + 1, parsedInvoiceNum + 1);
          batch.update(companyRef, {
            nonGstNextInvoiceNumber: nextSeq,
            updatedAt: serverTimestamp()
          });
        } else {
          const currentSeq = Number(compData.nextInvoiceNumber || 1);
          const nextSeq = Math.max(currentSeq + 1, parsedInvoiceNum + 1);
          batch.update(companyRef, {
            nextInvoiceNumber: nextSeq,
            updatedAt: serverTimestamp()
          });
        }
      }
    } catch (err) {
      console.warn("Could not inspect company sequence, falling back to atomic increment:", err);
      const fieldToIncrement = isNonGst ? "nonGstNextInvoiceNumber" : "nextInvoiceNumber";
      batch.update(companyRef, {
        [fieldToIncrement]: increment(1),
        updatedAt: serverTimestamp()
      });
    }
  }

  // Commit atomic batch
  try {
    await batch.commit();
  } catch (err) {
    writeAudit("INVOICE_CREATE_FAILED", {
      invoiceNo: invoiceData.invoiceNo,
      customerId: invoiceData.customerId,
      customerName: invoiceData.customerName,
      grandTotal: invoiceData.grandTotal,
      invoiceType: invoiceData.invoiceType
    }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("INVOICE_CREATED", {
    invoiceId: invoiceRef.id,
    invoiceNo: invoiceData.invoiceNo,
    customerId: invoiceData.customerId,
    customerName: invoiceData.customerName,
    grandTotal: invoiceData.grandTotal,
    balance: invoiceData.balance,
    invoiceType: invoiceData.invoiceType,
    companyId: invoiceData.companyId,
    lineItemsCount: (invoiceData.lineItems || []).length
  });

  return invoiceRef.id;
}

/**
 * Fetch all invoices ordered by creation date descending.
 * Safely handles documents with or without createdAt timestamp.
 */
export async function getInvoices() {
  // Single read of the whole collection (includes legacy docs without createdAt);
  // ordering is done below in JS, so no orderBy query is needed.
  const snapshot = await getDocs(collection(db, INVOICES_COLLECTION));
  const docs = snapshot.docs;

  const list = docs.map((d) => {
    const data = d.data();
    let createdAtIso = "";
    if (data.createdAt?.toDate) {
      createdAtIso = data.createdAt.toDate().toISOString();
    } else if (typeof data.createdAt === "string") {
      createdAtIso = data.createdAt;
    } else if (data.invoiceDate) {
      createdAtIso = `${data.invoiceDate}T00:00:00.000Z`;
    }

    const grandTotal = Number(data.grandTotal || 0);
    const paid = Number(data.paid !== undefined ? data.paid : (data.paidAmount !== undefined ? data.paidAmount : 0));
    const balance = Number(data.balance !== undefined ? data.balance : Math.max(0, grandTotal - paid));

    return {
      id: d.id,
      ...data,
      paid,
      balance,
      grandTotal,
      createdAt: createdAtIso
    };
  });

  // Sort descending: newest date first, fallback to invoiceNo descending
  return list.sort((a, b) => {
    const dateA = a.createdAt || a.invoiceDate || "";
    const dateB = b.createdAt || b.invoiceDate || "";
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    return String(b.invoiceNo || "").localeCompare(String(a.invoiceNo || ""), undefined, { numeric: true });
  });
}

/**
 * Fetch single invoice by ID
 */
export async function getInvoiceById(id) {
  const docRef = doc(db, INVOICES_COLLECTION, id);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Record a payment against an existing invoice
 */
export async function updateInvoicePayment(invoiceId, { paidAmount, customerId }) {
  const docRef = doc(db, INVOICES_COLLECTION, invoiceId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error("Invoice not found.");

  const current = snap.data();
  const currentPaid = Number(current.paid || 0);
  const total = Number(current.grandTotal || 0);
  const additionalPayment = Number(paidAmount || 0);

  const nextPaid = currentPaid + additionalPayment;
  const nextBalance = Math.max(0, total - nextPaid);
  const nextStatus = nextBalance === 0 ? "Paid" : "Partial";

  const batch = writeBatch(db);
  batch.update(docRef, {
    paid: nextPaid,
    balance: nextBalance,
    paymentStatus: nextStatus,
    updatedAt: serverTimestamp()
  });

  // Update customer ledger
  const targetCustId = customerId || current.customerId;
  if (targetCustId && additionalPayment > 0) {
    const custRef = doc(db, CUSTOMERS_COLLECTION, targetCustId);
    batch.update(custRef, {
      openingBalance: increment(-additionalPayment),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();
  return { nextPaid, nextBalance, nextStatus };
}

/** Fields an edit may never change: the issuing company and the invoice number / series. */
const LOCKED_FIELDS = ["invoiceNo", "invoiceType", "isNonGst", "companyId", "companyName", "companyGstin", "companyDetails", "sellerConfig", "defaultTemplate"];
const millis = (ts) => (ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : null);

/**
 * Saves an edited invoice in one transaction: keeps the previous version under invoices/{id}/revisions,
 * moves stock by the quantity difference, adjusts the customer's outstanding, keeps receipts as they are.
 * newData is the invoice payload from the create/edit screens; paidInFull = the "Paid in Full" toggle.
 * expectedUpdatedAt: the invoice's updatedAt when the edit screen opened (blocks overwriting someone else's change).
 */
export async function updateInvoice(invoiceId, newData, { expectedUpdatedAt = null, paidInFull = false } = {}) {
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";
  const invoiceRef = doc(db, INVOICES_COLLECTION, invoiceId);
  let result = null;

  try {
    result = await runTransaction(db, async (tx) => {
      const snap = await tx.get(invoiceRef);
      if (!snap.exists()) throw new Error("Invoice not found — it may have been deleted.");
      const old = snap.data();
      const expected = millis(expectedUpdatedAt);
      const current = millis(old.updatedAt);
      if (expected && current && expected !== current) {
        throw new Error("This invoice was changed elsewhere (e.g. a payment was recorded). Close and reopen it, then edit again.");
      }

      const { updatedItemPrices = {}, ...fields } = newData;
      LOCKED_FIELDS.forEach((k) => { if (k in old) fields[k] = old[k]; else delete fields[k]; });

      const change = diffInvoice(old, { ...fields, paidInFull });
      if (change.customerChanged && change.customerLocked) {
        throw new Error("Receipts are recorded for this invoice's customer. Void them before changing the customer.");
      }
      if (change.overReceived > 0) {
        const nos = change.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ");
        throw new Error(`Received ₹${change.newPaid.toLocaleString("en-IN")} is more than the new total ₹${change.newTotal.toLocaleString("en-IN")}. Edit or void ${nos || "the receipt"} first.`);
      }

      // Customer docs must exist before they are updated (reads first, then writes)
      const dueRefs = [];
      for (const d of change.dues) {
        const ref = doc(db, CUSTOMERS_COLLECTION, d.customerId);
        if ((await tx.get(ref)).exists()) dueRefs.push({ ref, delta: d.delta });
      }
      const itemRefs = [];
      for (const s of change.stock) {
        const ref = doc(db, ITEMS_COLLECTION, s.itemId);
        if ((await tx.get(ref)).exists()) itemRefs.push({ ref, s });
      }

      // 1. Keep the previous version (read-only history)
      const revisionRef = doc(collection(db, INVOICES_COLLECTION, invoiceId, "revisions"));
      tx.set(revisionRef, {
        snapshot: old,
        oldGrandTotal: change.oldTotal,
        newGrandTotal: change.newTotal,
        summary: change.summary,
        editedBy: user,
        editedAt: serverTimestamp()
      });

      // 2. Stock follows the quantity difference
      itemRefs.forEach(({ ref, s }) => {
        tx.update(ref, { stock: increment(s.stockChange), updatedAt: serverTimestamp() });
        tx.set(doc(collection(db, MOVEMENTS_COLLECTION)), {
          itemId: s.itemId,
          itemName: s.itemName,
          itemSku: "",
          type: s.stockChange < 0 ? MOVEMENT_TYPES.OUT : MOVEMENT_TYPES.IN,
          quantity: Math.abs(s.stockChange),
          previousStock: null,
          newStock: null,
          reason: "Invoice edited",
          notes: `Invoice ${old.invoiceNo || invoiceId}: qty ${s.oldQty} → ${s.newQty}`,
          referenceId: invoiceId,
          createdBy: user,
          createdAt: serverTimestamp()
        });
      });

      // 3. Customer outstanding
      dueRefs.forEach(({ ref, delta }) => tx.update(ref, { openingBalance: increment(delta), updatedAt: serverTimestamp() }));

      // 4. Rates remembered for this customer (same as on create)
      const prices = Object.entries(updatedItemPrices || {});
      if (fields.customerId && prices.length) {
        const priceUpdates = { updatedAt: serverTimestamp() };
        prices.forEach(([itemId, rate]) => { priceUpdates[`customPrices.${itemId}`] = Number(rate); });
        tx.update(doc(db, CUSTOMERS_COLLECTION, fields.customerId), priceUpdates);
      }

      // 5. The invoice itself — receipts (paid / paymentHistory) stay as recorded
      tx.update(invoiceRef, {
        ...fields,
        paid: change.newPaid,
        paidAmount: change.newPaid,
        balance: change.newBalance,
        paymentStatus: change.paymentStatus,
        paymentHistory: old.paymentHistory || [],
        editCount: increment(1),
        lastEditedAt: serverTimestamp(),
        lastEditedBy: user,
        updatedAt: serverTimestamp()
      });

      return { invoiceNo: old.invoiceNo, change };
    });
  } catch (err) {
    writeAudit("INVOICE_EDIT_FAILED", { invoiceId, invoiceNo: newData.invoiceNo }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("INVOICE_EDITED", {
    invoiceId,
    invoiceNo: result.invoiceNo,
    oldGrandTotal: result.change.oldTotal,
    newGrandTotal: result.change.newTotal,
    summary: result.change.summary.join("; ")
  });
  return result;
}

/** Earlier versions of an edited invoice, newest first. */
export async function getInvoiceRevisions(invoiceId) {
  const snap = await getDocs(collection(db, INVOICES_COLLECTION, invoiceId, "revisions"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (millis(b.editedAt) || 0) - (millis(a.editedAt) || 0));
}

/**
 * Atomically deletes an invoice and reverses stock deduction,
 * recording IN audit movements and adjusting customer ledger.
 */
export async function deleteInvoiceWithStockRestoration(invoiceId) {
  const docRef = doc(db, INVOICES_COLLECTION, invoiceId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error("Invoice not found.");

  const invoice = snap.data();
  const batch = writeBatch(db);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  // 1. Restore item stock & create IN movements for physical goods (only if item doc still exists)
  const lineItems = invoice.lineItems || [];
  for (const item of lineItems) {
    if (item.itemId && item.itemType !== "service") {
      try {
        const itemRef = doc(db, ITEMS_COLLECTION, item.itemId);
        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists()) {
          const qty = Number(item.qty || 0);
          batch.update(itemRef, {
            stock: increment(qty),
            updatedAt: serverTimestamp()
          });

          const movementRef = doc(collection(db, MOVEMENTS_COLLECTION));
          batch.set(movementRef, {
            itemId: item.itemId,
            itemName: item.itemName || "",
            itemSku: item.gstCode || "",
            type: MOVEMENT_TYPES.IN,
            quantity: qty,
            previousStock: null,
            newStock: null,
            reason: "Invoice Cancellation / Deletion",
            notes: `Stock restored from deleted invoice ${invoice.invoiceNo || invoiceId}`,
            referenceId: invoiceId,
            createdBy: user,
            createdAt: serverTimestamp()
          });
        }
      } catch (err) {
        console.warn(`Could not restore stock for item ${item.itemId}:`, err);
      }
    }
  }

  // 2. Adjust customer balance if invoice had unpaid balance and customer doc exists
  const unpaidBalance = Number(invoice.balance || 0);
  if (invoice.customerId && unpaidBalance > 0) {
    try {
      const custRef = doc(db, CUSTOMERS_COLLECTION, invoice.customerId);
      const custSnap = await getDoc(custRef);
      if (custSnap.exists()) {
        batch.update(custRef, {
          openingBalance: increment(-unpaidBalance),
          updatedAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.warn(`Could not adjust customer balance for ${invoice.customerId}:`, err);
    }
  }

  // 3. Delete invoice document
  batch.delete(docRef);

  try {
    await batch.commit();
  } catch (err) {
    writeAudit("INVOICE_DELETE_FAILED", {
      invoiceId,
      invoiceNo: invoice.invoiceNo,
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      grandTotal: invoice.grandTotal
    }, "ERROR", err?.message);
    throw err;
  }

  writeAudit("INVOICE_DELETED", {
    invoiceId,
    invoiceNo: invoice.invoiceNo,
    customerId: invoice.customerId,
    customerName: invoice.customerName,
    grandTotal: invoice.grandTotal,
    unpaidBalance,
    lineItemsCount: lineItems.length
  });

  return invoiceId;
}
